import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type KeyboardEvent,
} from 'react';
import type { MediaItem, WarpPresetV2 } from '@cornerstone/shared';
import {
  useCalibrationV2Store,
  useActiveSurfaceV2,
  useCanRedoV2,
  useCanUndoV2,
  useSaveStatusV2,
} from '../store/calibrationV2';
import {
  Button,
  Field,
  Kbd,
  Panel,
  SaveStatusBadge,
  Segmented,
  StatusBadge,
} from '../components/ui';
import { DPad } from '../components/DPad';
import { SurfaceCanvasV2 } from '../components/SurfaceCanvasV2';
import { SourceMapV2 } from '../components/SourceMapV2';
import { TargetPickerModal } from '../components/TargetPickerModal';
import { useCalibrationV2Shortcuts } from '../hooks/useCalibrationV2Shortcuts';
import { useLiveWarpSyncV2 } from '../hooks/useLiveWarpSyncV2';
import { useMediaTextureV2 } from '../renderer/useWarpV2Renderer';
import { api } from '../client/api';
import { apiV2 } from '../client/apiV2';
import { toast, describeApiError } from '../components/toast';
import { cn } from '../utils/cn';
import { backdropCloseProps } from '../components/menu';
import { ContentSkeleton } from '../components/skeleton/ContentSkeleton';

// =============================================================================
// Calibration v2 — PLAN P3 (docs/old/CALIBRATION_V2_POLYGON_PLAN.md §5).
// 코너핀 / 메시 / 마스크 3모드 편집기 + 실제 미디어 프리뷰(§4.3) +
// surface content 바인딩(§10) + v2 preset CRUD.
// =============================================================================

const MASK_SHAPES = [
  { kind: 'rect', glyph: '▭', label: '사각' },
  { kind: 'diamond', glyph: '◇', label: '마름모' },
  { kind: 'star', glyph: '★', label: '별' },
  { kind: 'circle', glyph: '●', label: '원' },
  { kind: 'custom', glyph: '✎', label: '커스텀' },
] as const;

const MESH_DENSITIES = [2, 4, 8] as const;

export function Calibration({ deviceId }: { deviceId?: string | undefined } = {}) {
  useLiveWarpSyncV2();

  // ----- Bootstrap: presets + live state + media list -----
  const loadPresets = useCalibrationV2Store((s) => s.loadPresets);
  const hydrateLive = useCalibrationV2Store((s) => s.hydrateLive);
  const setLiveWarpSyncReady = useCalibrationV2Store((s) => s.setLiveWarpSyncReady);
  const setStoreDeviceId = useCalibrationV2Store((s) => s.setDeviceId);

  const [mediaList, setMediaList] = useState<MediaItem[]>([]);
  const [initialLoading, setInitialLoading] = useState(Boolean(deviceId));
  const [bootstrapError, setBootstrapError] = useState(false);
  const [bootstrapRevision, setBootstrapRevision] = useState(0);
  const liveWarpSyncReady = useCalibrationV2Store(s => s.liveWarpSyncReady);
  const saving = useCalibrationV2Store(s => s.saving);

  useEffect(() => {
    let cancelled = false;
    setLiveWarpSyncReady(false); setBootstrapError(false);
    // 기기별 격리 — 대상 디바이스를 store 에 먼저 고정해야 preset/live 호출이
    // 그 기기 범위로만 나간다. deviceId 없이는 서버 동기화 자체를 막는다.
    setStoreDeviceId(deviceId ?? null);
    if (!deviceId) { setInitialLoading(false); return; }
    setInitialLoading(true);
    void (async () => {
      let hydrated = false;
      try {
        const loadedPresets = await loadPresets();
        if (cancelled) return;
        if (useCalibrationV2Store.getState().presetError) throw new Error('presets unavailable');
        const serverState = await apiV2.getLiveState(deviceId);
        if (cancelled) return;
        if (serverState) {
          const savedPreset = loadedPresets.find(p => p.id === serverState.activePresetId);
          // Live edits take precedence; the saved row only supplies the dirty baseline.
          hydrateLive({ ...serverState.live, activePresetId: serverState.activePresetId,
            ...(savedPreset ? { savedPreset } : {}) });
          hydrated = true;
        }
      } catch {
        if (!cancelled) setBootstrapError(true);
      } finally {
        if (!cancelled) {
          if (hydrated) setLiveWarpSyncReady(true);
          setInitialLoading(false);
        }
      }
    })();
    void api
      .listMedia()
      .then((list) => {
        if (!cancelled) setMediaList(list);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
      // 페이지 이탈 시 대상 기기 해제 — 잔여 sync 가 다른 화면에서 이 기기를
      // 계속 덮어쓰지 않게 한다.
      setStoreDeviceId(null);
      setLiveWarpSyncReady(false);
    };
  }, [deviceId, bootstrapRevision, loadPresets, hydrateLive, setLiveWarpSyncReady, setStoreDeviceId]);

  // ----- Store subscriptions -----
  const surface = useActiveSurfaceV2();
  const surfaces = useCalibrationV2Store((s) => s.surfaces);
  const activeSurfaceId = useCalibrationV2Store((s) => s.activeSurfaceId);
  const setActiveSurface = useCalibrationV2Store((s) => s.setActiveSurface);
  const addSurface = useCalibrationV2Store((s) => s.addSurface);
  const removeActiveSurface = useCalibrationV2Store((s) => s.removeActiveSurface);
  const applySurfaceGrid = useCalibrationV2Store((s) => s.applySurfaceGrid);
  const setSurfaceColor = useCalibrationV2Store((s) => s.setSurfaceColor);
  const setSourceRegion = useCalibrationV2Store((s) => s.setSourceRegion);
  const setSurfaceMediaTransform = useCalibrationV2Store((s) => s.setSurfaceMediaTransform);
  const editorMode = useCalibrationV2Store((s) => s.editorMode);
  const setEditorMode = useCalibrationV2Store((s) => s.setEditorMode);
  const selection = useCalibrationV2Store((s) => s.selection);
  const nudgeStep = useCalibrationV2Store((s) => s.nudgeStep);
  const setNudgeStep = useCalibrationV2Store((s) => s.setNudgeStep);
  const nudgeSelected = useCalibrationV2Store((s) => s.nudgeSelected);
  const moveCorner = useCalibrationV2Store((s) => s.moveCorner);
  const moveMeshPoint = useCalibrationV2Store((s) => s.moveMeshPoint);
  const moveMaskVertex = useCalibrationV2Store((s) => s.moveMaskVertex);

  const testPattern = useCalibrationV2Store((s) => s.testPattern);
  const setTestPattern = useCalibrationV2Store((s) => s.setTestPattern);
  const previewSource = useCalibrationV2Store((s) => s.previewSource);
  const setPreviewSource = useCalibrationV2Store((s) => s.setPreviewSource);

  const requestMeshDensity = useCalibrationV2Store((s) => s.requestMeshDensity);
  const pendingMeshDensity = useCalibrationV2Store((s) => s.pendingMeshDensity);
  const confirmMeshDensity = useCalibrationV2Store((s) => s.confirmMeshDensity);
  const cancelMeshDensity = useCalibrationV2Store((s) => s.cancelMeshDensity);
  const resetMesh = useCalibrationV2Store((s) => s.resetMesh);

  const applyMaskShape = useCalibrationV2Store((s) => s.applyMaskShape);
  const clearMask = useCalibrationV2Store((s) => s.clearMask);
  const setMaskFeather = useCalibrationV2Store((s) => s.setMaskFeather);
  const setMaskInvert = useCalibrationV2Store((s) => s.setMaskInvert);
  const resizeMask = useCalibrationV2Store((s) => s.resizeMask);
  const moveMask = useCalibrationV2Store((s) => s.moveMask);
  const toggleMaskVertexCurve = useCalibrationV2Store((s) => s.toggleMaskVertexCurve);
  const deleteMaskVertices = useCalibrationV2Store((s) => s.deleteMaskVertices);

  const setSurfaceContent = useCalibrationV2Store((s) => s.setSurfaceContent);
  const bypassWarp = useCalibrationV2Store((s) => s.bypassWarp);
  const setBypassWarp = useCalibrationV2Store((s) => s.setBypassWarp);

  const undo = useCalibrationV2Store((s) => s.undo);
  const redo = useCalibrationV2Store((s) => s.redo);
  const resetToDefault = useCalibrationV2Store((s) => s.resetToDefault);
  const revertToSaved = useCalibrationV2Store((s) => s.revertToSaved);
  const connectionStatus = useCalibrationV2Store((s) => s.connectionStatus);
  const presets = useCalibrationV2Store((s) => s.presets);
  const activePresetId = useCalibrationV2Store((s) => s.activePresetId);
  const presetError = useCalibrationV2Store((s) => s.presetError);
  const clearPresetError = useCalibrationV2Store((s) => s.clearPresetError);
  const saveOverwrite = useCalibrationV2Store((s) => s.saveOverwrite);
  const saveAs = useCalibrationV2Store((s) => s.saveAs);
  const applyPresetById = useCalibrationV2Store((s) => s.applyPresetById);
  const duplicatePresetById = useCalibrationV2Store((s) => s.duplicatePresetById);
  const deletePresetById = useCalibrationV2Store((s) => s.deletePresetById);
  const togglePresetProtected = useCalibrationV2Store((s) => s.togglePresetProtected);
  const exportPresets = useCalibrationV2Store((s) => s.exportPresets);
  const importPresets = useCalibrationV2Store((s) => s.importPresets);

  const canUndo = useCanUndoV2();
  const canRedo = useCanRedoV2();
  const saveStatus = useSaveStatusV2();

  // ----- Media preview (§4.3) -----
  const previewMediaItem = useMemo(
    () =>
      previewSource.kind === 'media'
        ? mediaList.find((m) => m.id === previewSource.mediaId) ?? null
        : null,
    [previewSource, mediaList],
  );
  const mediaTexture = useMediaTextureV2(previewMediaItem);

  // ----- Selected point readout -----
  const selectedPoint = useMemo(() => {
    if (!surface || !selection) return null;
    if (selection.kind === 'corner') return surface.corners[selection.index];
    if (selection.kind === 'mesh' && selection.indices.length === 1) {
      return surface.mesh?.points[selection.indices[0]!] ?? null;
    }
    if (selection.kind === 'maskVertex' && selection.indices.length === 1) {
      return surface.mask?.vertices[selection.indices[0]!]?.p ?? null;
    }
    return null;
  }, [surface, selection]);

  const selectionLabel = useMemo(() => {
    if (!selection) return null;
    if (selection.kind === 'corner') {
      return `코너 · ${(['TL', 'TR', 'BR', 'BL'] as const)[selection.index]}`;
    }
    if (selection.kind === 'mesh') {
      return selection.indices.length === 1
        ? `메시 점 #${selection.indices[0]}`
        : `메시 점 ${selection.indices.length}개`;
    }
    return selection.indices.length === 1
      ? `마스크 정점 #${selection.indices[0]}`
      : `마스크 정점 ${selection.indices.length}개`;
  }, [selection]);

  const commitSelectedPoint = useCallback(
    (patch: { x?: number; y?: number }) => {
      if (!surface || !selection || !selectedPoint) return;
      const next = {
        x: patch.x ?? selectedPoint.x,
        y: patch.y ?? selectedPoint.y,
      };
      if (selection.kind === 'corner') moveCorner(selection.index, next);
      else if (selection.kind === 'mesh' && selection.indices.length === 1) {
        moveMeshPoint(selection.indices[0]!, next);
      } else if (selection.kind === 'maskVertex' && selection.indices.length === 1) {
        moveMaskVertex(selection.indices[0]!, next);
      }
    },
    [surface, selection, selectedPoint, moveCorner, moveMeshPoint, moveMaskVertex],
  );

  const onNudgeDpad = (dx: -1 | 0 | 1, dy: -1 | 0 | 1) => {
    const step = nudgeStep === 0.1 ? 0.0001 : nudgeStep === 10 ? 0.01 : 0.001;
    nudgeSelected(dx * step, dy * step);
  };

  // ----- In-app modals (window.prompt/confirm 금지) -----
  const [promptState, setPromptState] = useState<PromptState | null>(null);
  const [confirmState, setConfirmState] = useState<ConfirmState | null>(null);
  const [mediaPickerFor, setMediaPickerFor] = useState<'preview' | 'content' | null>(
    null,
  );
  const [maskAspectLocked, setMaskAspectLocked] = useState(true);
  const importFileRef = useRef<HTMLInputElement | null>(null);

  const askPrompt = useCallback(
    (opts: { title: string; initial: string; submitLabel?: string }) =>
      new Promise<string | null>((resolve) => {
        setPromptState({ ...opts, resolve });
      }),
    [],
  );
  const askConfirm = useCallback(
    (opts: { title: string; message: string; destructive?: boolean }) =>
      new Promise<boolean>((resolve) => {
        setConfirmState({ ...opts, resolve });
      }),
    [],
  );

  // ----- Save handlers (v1 save guard 이식: protected / 24h 경과) -----
  const handleSave = useCallback(async () => {
    if (!useCalibrationV2Store.getState().liveWarpSyncReady) return;
    if (activePresetId) {
      const store = useCalibrationV2Store.getState();
      const preset = store.presets.find((p) => p.id === activePresetId);
      if (preset) {
        const reasons: string[] = [];
        if (preset.protected) reasons.push('• 이 preset은 보호됨 (protected)');
        const ageMs = Date.now() - new Date(preset.lastSavedAt).getTime();
        if (ageMs > 24 * 60 * 60 * 1000) {
          const days = Math.floor(ageMs / (24 * 60 * 60 * 1000));
          reasons.push(`• 마지막 저장 후 ${days}일 이상 경과`);
        }
        if (reasons.length > 0) {
          const ok = await askConfirm({
            title: `'${preset.name}' 덮어쓰기?`,
            message:
              reasons.join('\n') + '\n\nSave As…로 새 preset 저장도 가능합니다.',
            destructive: preset.protected,
          });
          if (!ok) return;
        }
      }
      const updated = await saveOverwrite();
      if (updated) toast('저장되었습니다. 재시작 후에도 자동 적용됩니다.', 'success');
    } else {
      const name = await askPrompt({
        title: 'Save preset',
        initial: `Preset ${new Date().toLocaleString()}`,
        submitLabel: 'Save',
      });
      if (name && name.trim()) {
        const created = await saveAs(name.trim());
        if (created) toast('저장되었습니다. 재시작 후에도 자동 적용됩니다.', 'success');
      }
    }
  }, [activePresetId, saveOverwrite, saveAs, askPrompt, askConfirm]);

  const handleSaveAs = useCallback(async () => {
    if (!useCalibrationV2Store.getState().liveWarpSyncReady) return;
    const name = await askPrompt({
      title: 'Save as new preset',
      initial: `Preset ${new Date().toLocaleString()}`,
      submitLabel: 'Save',
    });
    if (name && name.trim()) {
      const created = await saveAs(name.trim());
      if (created) toast('저장되었습니다. 재시작 후에도 자동 적용됩니다.', 'success');
    }
  }, [saveAs, askPrompt]);

  const handleImportFile = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      e.target.value = '';
      if (!file) return;
      try {
        const count = await importPresets(file);
        toast(`${count}개의 preset을 가져왔습니다.`, 'success');
      } catch {
        // importPresets가 이미 toast + presetError 처리.
      }
    },
    [importPresets],
  );

  const shortcutCallbacks = useMemo(
    () => ({
      onSave: () => void handleSave(),
      onSaveAs: () => void handleSaveAs(),
    }),
    [handleSave, handleSaveAs],
  );
  useCalibrationV2Shortcuts(shortcutCallbacks);

  const mask = surface?.mask ?? null;
  const maskBounds = useMemo(() => {
    if (!mask || mask.vertices.length === 0) return null;
    const xs = mask.vertices.map((v) => v.p.x);
    const ys = mask.vertices.map((v) => v.p.y);
    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    const minY = Math.min(...ys);
    const maxY = Math.max(...ys);
    return {
      width: maxX - minX,
      height: maxY - minY,
      centerX: (minX + maxX) / 2,
      centerY: (minY + maxY) / 2,
    };
  }, [mask]);
  const mesh = surface?.mesh ?? null;
  const content = surface?.content ?? { type: 'schedule' as const };
  const mediaTransform = surface?.mediaTransform ?? { scale: 1, offsetX: 0, offsetY: 0 };
  const previewMediaAspect = mediaTexture
    ? mediaTexture.width / Math.max(1, mediaTexture.height)
    : null;
  const fillScale = previewMediaAspect
    ? Math.min(3, Math.max(previewMediaAspect / (16 / 9), (16 / 9) / previewMediaAspect))
    : 1;
  const singleMaskVertex =
    selection?.kind === 'maskVertex' && selection.indices.length === 1
      ? selection.indices[0]!
      : null;
  const selectedVertexCurved =
    singleMaskVertex !== null
      ? Boolean(
          mask?.vertices[singleMaskVertex]?.h1 || mask?.vertices[singleMaskVertex]?.h2,
        )
      : false;

  const contentMediaName =
    content.type === 'media'
      ? mediaList.find((m) => m.id === content.mediaId)?.originalName ??
        content.mediaId
      : null;

  if (initialLoading) return <div className="h-full overflow-auto bg-bg p-6">
    <ContentSkeleton label="캘리브레이션 화면을 불러오는 중…" variant="split" />
  </div>;

  return (
    <div className="h-screen flex flex-col overflow-hidden bg-bg">
      {connectionStatus !== 'online' && (
        <div className="px-5 py-1.5 bg-red/10 border-b border-red/40 text-red font-mono text-[13px] uppercase tracking-[0.1em] flex items-center justify-between flex-none">
          <span>
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-red mr-2 align-middle" />
            {connectionStatus === 'connecting'
              ? 'Connecting to main server...'
              : 'Main server offline — 변경은 Admin state에만 저장됩니다.'}
          </span>
          <span className="opacity-70 normal-case tracking-normal">auto-reconnect 2s</span>
        </div>
      )}
      {!liveWarpSyncReady && <div role="status" className="px-5 py-3 border-b border-line text-[14px] flex gap-3 items-center flex-none">
        <span>{bootstrapError ? '현재 캘리브레이션을 확인하지 못했습니다. 다시 확인한 뒤 편집해 주세요.'
          : presets.length ? '자동 적용된 캘리브레이션이 없습니다. 저장된 프리셋에서 적용을 선택해 주세요.'
          : '아직 적용된 캘리브레이션이 없습니다.'}</span>
        <Button onClick={() => setBootstrapRevision(value => value + 1)}>다시 확인</Button>
        {!bootstrapError && presets.length === 0 && <Button onClick={() => setLiveWarpSyncReady(true)}>새 캘리브레이션 시작</Button>}
      </div>}
      {saving && <p role="status" className="px-5 py-2 text-[13px]">캘리브레이션과 자동 복원 설정을 저장하는 중…</p>}
      {presetError && (
        <div className="px-5 py-1.5 bg-red/10 border-b border-red/40 text-red font-mono text-[13px] flex items-center justify-between flex-none">
          <span>⚠ {presetError}</span>
          <button
            type="button"
            onClick={clearPresetError}
            className="opacity-70 hover:opacity-100"
          >
            ✕
          </button>
        </div>
      )}

      <Topbar
        deviceId={deviceId}
        saveStatus={saveStatus}
        canUndo={canUndo}
        canRedo={canRedo}
        connectionStatus={connectionStatus}
        onUndo={undo}
        onRedo={redo}
        onReset={() =>
          void askConfirm({
            title: 'Reset calibration?',
            message: '코너·메시·마스크가 기본값으로 초기화됩니다.\nUndo (⌘Z)로 복구 가능합니다.',
          }).then((ok) => {
            if (ok) resetToDefault();
          })
        }
        onRevert={revertToSaved}
        onSave={() => void handleSave()}
        onSaveAs={() => void handleSaveAs()}
      />

      {/* Main workspace — 3-column grid */}
      <div className="flex-1 min-h-0 grid grid-cols-[250px_1fr_300px] gap-3 px-5 py-3">
        {/* ============ LEFT — 모드 + 소스 + 모드별 패널 ============ */}
        <aside className="flex flex-col gap-3 min-h-0 overflow-y-auto pr-1">
          <div className="flex-none">
            <Panel title="Edit Mode" trailing={<Kbd subtle>1·2·3</Kbd>}>
              <Segmented
                value={editorMode}
                onChange={setEditorMode}
                options={[
                  { value: 'corner', label: '서페이스' },
                  { value: 'mesh', label: '메시' },
                  { value: 'mask', label: '마스크' },
                ]}
              />
            </Panel>
          </div>

          {/* 소스 — 테스트 패턴 | 미디어 선택 (§4.3) */}
          <div className="flex-none">
            <Panel title="Source" trailing={<Kbd subtle>T</Kbd>}>
              <Segmented
                className="mb-2"
                value={previewSource.kind}
                onChange={(v) => {
                  if (v === 'pattern') setPreviewSource({ kind: 'pattern' });
                  else setMediaPickerFor('preview');
                }}
                options={[
                  { value: 'pattern', label: '패턴' },
                  { value: 'media', label: '미디어' },
                ]}
              />
              {previewSource.kind === 'pattern' ? (
                <Segmented
                  value={testPattern}
                  onChange={setTestPattern}
                  options={[
                    { value: 'none', label: 'Off' },
                    { value: 'checker', label: 'Chk' },
                    { value: 'grid', label: 'Grid' },
                    { value: 'crosshair', label: '+' },
                  ]}
                />
              ) : (
                <div className="flex flex-col gap-2">
                  <div className="font-mono text-[12px] text-text-dim truncate">
                    {previewMediaItem
                      ? previewMediaItem.originalName
                      : '미디어를 선택하세요'}
                  </div>
                  <div className="flex gap-1.5">
                    <Button
                      className="!px-2.5 !py-1 !text-[13px]"
                      onClick={() => setMediaPickerFor('preview')}
                    >
                      미디어 선택…
                    </Button>
                  </div>
                </div>
              )}
            </Panel>
          </div>

          <div className="flex-none">
            <Panel
              title="Source Map"
              trailing={<span className="font-mono text-[11px] text-text-dim">{surfaces.length} surfaces</span>}
            >
              <SourceMapV2
                mediaUrl={previewMediaItem ? api.mediaFileUrl(previewMediaItem.id) : null}
                mediaType={previewMediaItem?.mediaType === 'video' ? 'video' : previewMediaItem ? 'image' : null}
              />
              <div className="mt-2">
                <div className="mb-1 font-mono text-[10px] uppercase tracking-monoWide text-text-dim">
                  화면 자동 분할
                </div>
                <div className="grid grid-cols-4 gap-1">
                  {[
                    { label: '1', cols: 1, rows: 1 },
                    { label: '1×2', cols: 2, rows: 1 },
                    { label: '2×1', cols: 1, rows: 2 },
                    { label: '2×2', cols: 2, rows: 2 },
                  ].map((layout) => (
                    <Button
                      key={layout.label}
                      className="!px-1 !py-1 !text-[11px]"
                      onClick={() =>
                        void askConfirm({
                          title: `${layout.label} 화면으로 분할?`,
                          message: '현재 Surface 배치가 새 분할 레이아웃으로 교체됩니다.\nUndo (⌘Z)로 복구 가능합니다.',
                        }).then((ok) => {
                          if (ok) applySurfaceGrid(layout.cols, layout.rows);
                        })
                      }
                    >
                      {layout.label}
                    </Button>
                  ))}
                </div>
              </div>
              <div className="mt-2 flex gap-1.5">
                <Button className="!px-2 !py-1 !text-[12px]" onClick={addSurface}>
                  + Surface
                </Button>
                <Button
                  className="!px-2 !py-1 !text-[12px]"
                  disabled={surfaces.length <= 1}
                  onClick={removeActiveSurface}
                >
                  삭제
                </Button>
              </div>
              <div className="mt-2 flex flex-col gap-1">
                {surfaces.map((item, index) => {
                  const active = item.id === activeSurfaceId;
                  return (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => setActiveSurface(item.id)}
                      className={cn(
                        'flex items-center gap-2 rounded border px-2 py-1.5 text-left font-mono text-[12px]',
                        active ? 'border-accent bg-accent-soft text-text' : 'border-line text-text-dim',
                      )}
                    >
                      <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: item.color ?? '#38BDF8' }} />
                      <span className="flex-1">Surface {index + 1}</span>
                      <span>{Math.round((item.sourceRegion?.width ?? 1) * 100)}%</span>
                    </button>
                  );
                })}
              </div>
              {surface && (
                <>
                  <div className="mt-2 grid grid-cols-4 gap-1">
                    {(['x', 'y', 'width', 'height'] as const).map((key) => {
                      const region = surface.sourceRegion ?? { x: 0, y: 0, width: 1, height: 1 };
                      return (
                        <label key={key} className="font-mono text-[10px] uppercase text-text-dim">
                          {key === 'width' ? 'W' : key === 'height' ? 'H' : key.toUpperCase()}
                          <input
                            type="number"
                            min={0}
                            max={100}
                            step={1}
                            value={Math.round(region[key] * 100)}
                            onChange={(event) =>
                              setSourceRegion({ ...region, [key]: Number(event.target.value) / 100 })
                            }
                            className="mt-1 w-full rounded border border-line bg-panel-2 px-1 py-1 text-[11px] text-text"
                          />
                        </label>
                      );
                    })}
                  </div>
                  <label className="mt-2 flex items-center justify-between font-mono text-[11px] text-text-dim">
                    Surface color
                    <input
                      type="color"
                      value={surface.color ?? '#38BDF8'}
                      onChange={(event) => setSurfaceColor(event.target.value)}
                      className="h-7 w-10 cursor-pointer rounded border border-line bg-transparent"
                    />
                  </label>
                </>
              )}
            </Panel>
          </div>

          {/* 메시 모드 패널 */}
          {editorMode === 'mesh' && (
            <div className="flex-none">
              <Panel title="Mesh Grid">
                <div className="mb-2 font-mono text-[11px] uppercase tracking-monoWide text-text-dim">
                  Density
                </div>
                <Segmented
                  value={mesh?.cols ?? 0}
                  onChange={(v) => {
                    if (v !== 0) requestMeshDensity(v);
                  }}
                  options={MESH_DENSITIES.map((n) => ({
                    value: n as number,
                    label: `${n}×${n}`,
                  }))}
                />
                <div className="mt-3">
                  <Button
                    className="!px-2.5 !py-1 !text-[13px]"
                    disabled={!mesh}
                    onClick={() =>
                      void askConfirm({
                        title: '그리드 초기화?',
                        message:
                          '모든 메시 점이 균등 격자(identity)로 돌아갑니다.\nUndo (⌘Z)로 복구 가능합니다.',
                        destructive: true,
                      }).then((ok) => {
                        if (ok) resetMesh();
                      })
                    }
                  >
                    그리드 초기화
                  </Button>
                </div>
                <p className="mt-2 font-mono text-[12px] text-text-dimmer leading-snug">
                  점 드래그로 굴곡 보정 · Shift+클릭 다중 선택 · Shift+드래그 축 제한
                </p>
              </Panel>
            </div>
          )}

          {/* 마스크 모드 패널 */}
          {editorMode === 'mask' && (
            <div className="flex-none">
              <Panel
                title="Mask"
                trailing={
                  mask?.kind === 'custom' ? (
                    <span className="font-mono text-[11px] uppercase tracking-monoWide text-accent">
                      커스텀으로 전환됨
                    </span>
                  ) : undefined
                }
              >
                <div className="flex gap-1 mb-3">
                  {MASK_SHAPES.map((s) => (
                    <button
                      key={s.kind}
                      type="button"
                      title={s.label}
                      onClick={() => applyMaskShape(s.kind)}
                      className={cn(
                        'w-9 h-9 border rounded-sm text-[16px] flex items-center justify-center',
                        'transition-colors duration-fast ease-snap',
                        mask?.kind === s.kind
                          ? 'border-accent text-accent bg-accent-soft'
                          : 'border-line text-text-dim hover:text-text hover:border-line-strong',
                      )}
                    >
                      {s.glyph}
                    </button>
                  ))}
                  <button
                    type="button"
                    title="마스크 제거"
                    onClick={clearMask}
                    disabled={!mask}
                    className={cn(
                      'w-9 h-9 border border-line rounded-sm text-[14px] flex items-center justify-center',
                      'text-text-dim hover:text-red hover:border-red transition-colors duration-fast',
                      !mask && 'opacity-40 cursor-not-allowed',
                    )}
                  >
                    ✕
                  </button>
                </div>

                {mask && (
                  <>
                    {maskBounds && (
                      <div className="mb-3 rounded-sm border border-line bg-panel-raised/40 p-2.5">
                        <div className="mb-2 flex items-center justify-between">
                          <span className="font-mono text-[11px] uppercase tracking-monoWide text-text-dim">
                            Mask Size
                          </span>
                          <button
                            type="button"
                            onClick={() => setMaskAspectLocked((locked) => !locked)}
                            className={cn(
                              'rounded-sm border px-2 py-1 font-mono text-[10px] uppercase tracking-monoWide',
                              maskAspectLocked
                                ? 'border-accent text-accent bg-accent-soft'
                                : 'border-line text-text-dim',
                            )}
                          >
                            비율 {maskAspectLocked ? '고정' : '해제'}
                          </button>
                        </div>
                        <div className="mb-2">
                          <div className="mb-1 flex justify-between font-mono text-[11px] text-text-dim">
                            <span>Width</span>
                            <span className="text-accent">{Math.round(maskBounds.width * 100)}%</span>
                          </div>
                          <input
                            type="range"
                            min={1}
                            max={100}
                            step={1}
                            value={Math.round(maskBounds.width * 100)}
                            onChange={(e) => {
                              const width = Number(e.target.value) / 100;
                              const ratio = width / Math.max(maskBounds.width, 0.01);
                              resizeMask(
                                width,
                                maskAspectLocked ? maskBounds.height * ratio : maskBounds.height,
                              );
                            }}
                            className="w-full accent-[#F2A93E]"
                          />
                        </div>
                        <div>
                          <div className="mb-1 flex justify-between font-mono text-[11px] text-text-dim">
                            <span>Height</span>
                            <span className="text-accent">{Math.round(maskBounds.height * 100)}%</span>
                          </div>
                          <input
                            type="range"
                            min={1}
                            max={100}
                            step={1}
                            value={Math.round(maskBounds.height * 100)}
                            onChange={(e) => {
                              const height = Number(e.target.value) / 100;
                              const ratio = height / Math.max(maskBounds.height, 0.01);
                              resizeMask(
                                maskAspectLocked ? maskBounds.width * ratio : maskBounds.width,
                                height,
                              );
                            }}
                            className="w-full accent-[#F2A93E]"
                          />
                        </div>
                        <div className="my-2 border-t border-line" />
                        <div className="mb-2 font-mono text-[11px] uppercase tracking-monoWide text-text-dim">
                          Mask Position
                        </div>
                        <div className="mb-2">
                          <div className="mb-1 flex justify-between font-mono text-[11px] text-text-dim">
                            <span>Position X</span>
                            <MaskPositionInput
                              value={maskBounds.centerX}
                              onCommit={(value) => moveMask(value, maskBounds.centerY)}
                            />
                          </div>
                          <input
                            type="range"
                            min={Math.round((maskBounds.width / 2) * 100)}
                            max={Math.round((1 - maskBounds.width / 2) * 100)}
                            step={1}
                            value={Math.round(maskBounds.centerX * 100)}
                            onChange={(e) =>
                              moveMask(Number(e.target.value) / 100, maskBounds.centerY)
                            }
                            className="w-full accent-[#F2A93E]"
                          />
                        </div>
                        <div>
                          <div className="mb-1 flex justify-between font-mono text-[11px] text-text-dim">
                            <span>Position Y</span>
                            <MaskPositionInput
                              value={maskBounds.centerY}
                              onCommit={(value) => moveMask(maskBounds.centerX, value)}
                            />
                          </div>
                          <input
                            type="range"
                            min={Math.round((maskBounds.height / 2) * 100)}
                            max={Math.round((1 - maskBounds.height / 2) * 100)}
                            step={1}
                            value={Math.round(maskBounds.centerY * 100)}
                            onChange={(e) =>
                              moveMask(maskBounds.centerX, Number(e.target.value) / 100)
                            }
                            className="w-full accent-[#F2A93E]"
                          />
                        </div>
                      </div>
                    )}
                    <div className="mb-3">
                      <div className="flex justify-between font-mono text-[11px] uppercase tracking-monoWide text-text-dim mb-1.5">
                        <span>Feather</span>
                        <span className="text-accent">{Math.round(mask.feather)}px</span>
                      </div>
                      <input
                        type="range"
                        min={0}
                        max={200}
                        step={1}
                        value={mask.feather}
                        onChange={(e) => setMaskFeather(Number(e.target.value))}
                        className="w-full accent-[#F2A93E]"
                      />
                    </div>

                    <div className="mb-3 flex items-center justify-between">
                      <span className="font-mono text-[11px] uppercase tracking-monoWide text-text-dim">
                        Invert
                      </span>
                      <Segmented
                        value={mask.invert ? 'on' : 'off'}
                        onChange={(v) => setMaskInvert(v === 'on')}
                        options={[
                          { value: 'off', label: 'Off' },
                          { value: 'on', label: 'On' },
                        ]}
                      />
                    </div>

                    <div className="flex gap-1.5">
                      <Button
                        className="!px-2.5 !py-1 !text-[13px]"
                        disabled={singleMaskVertex === null}
                        onClick={() => {
                          if (singleMaskVertex !== null)
                            toggleMaskVertexCurve(singleMaskVertex);
                        }}
                      >
                        {selectedVertexCurved ? '→ 직선' : '→ 곡선'}
                      </Button>
                      <Button
                        className="!px-2.5 !py-1 !text-[13px]"
                        disabled={
                          selection?.kind !== 'maskVertex' ||
                          mask.vertices.length - (selection?.kind === 'maskVertex' ? selection.indices.length : 0) < 3
                        }
                        onClick={() => {
                          if (selection?.kind === 'maskVertex')
                            deleteMaskVertices(selection.indices);
                        }}
                      >
                        정점 삭제
                      </Button>
                    </div>
                    <p className="mt-2 font-mono text-[12px] text-text-dimmer leading-snug">
                      엣지 더블클릭 = 정점 추가 · 정점 우클릭 = 직선↔곡선 · Delete = 삭제
                    </p>
                  </>
                )}
              </Panel>
            </div>
          )}

          {/* Surface content (§10) */}
          <div className="flex-none">
            <Panel title="Surface Content">
              <select
                value={content.type}
                onChange={(e) => {
                  const t = e.target.value as 'schedule' | 'media' | 'color';
                  if (t === 'schedule') setSurfaceContent({ type: 'schedule' });
                  else if (t === 'color')
                    setSurfaceContent({ type: 'color', color: '#000000' });
                  else setMediaPickerFor('content');
                }}
                className="w-full bg-panel-2 border border-line rounded px-2.5 py-2 text-[14px] text-text mb-2"
              >
                <option value="schedule">스케줄 재생 (기본)</option>
                <option value="media">고정 미디어 루프</option>
                <option value="color">단색</option>
              </select>
              {content.type === 'media' && (
                <div className="flex items-center gap-2">
                  <span className="flex-1 font-mono text-[12px] text-text-dim truncate">
                    {contentMediaName ?? '미디어 미선택'}
                  </span>
                  <Button
                    className="!px-2.5 !py-1 !text-[13px]"
                    onClick={() => setMediaPickerFor('content')}
                  >
                    변경…
                  </Button>
                </div>
              )}
              {content.type === 'color' && (
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={content.color}
                    onChange={(e) =>
                      setSurfaceContent({ type: 'color', color: e.target.value })
                    }
                    className="w-9 h-9 bg-transparent border border-line rounded cursor-pointer"
                  />
                  <span className="font-mono text-[13px] text-text-dim">
                    {content.color}
                  </span>
                </div>
              )}
            </Panel>
          </div>

          <div className="flex-none">
            <Panel title="Media Transform">
              <div className="mb-3">
                <div className="mb-1.5 flex justify-between font-mono text-[11px] uppercase text-text-dim">
                  <span>Zoom</span>
                  <span className="text-accent">{Math.round(mediaTransform.scale * 100)}%</span>
                </div>
                <input
                  type="range"
                  min={100}
                  max={300}
                  step={1}
                  value={Math.round(mediaTransform.scale * 100)}
                  onChange={(event) =>
                    setSurfaceMediaTransform({
                      ...mediaTransform,
                      scale: Number(event.target.value) / 100,
                    })
                  }
                  className="w-full accent-[#F24822]"
                />
              </div>
              {(['offsetX', 'offsetY'] as const).map((key) => (
                <div key={key} className="mb-2">
                  <div className="mb-1 flex justify-between font-mono text-[10px] uppercase text-text-dim">
                    <span>{key === 'offsetX' ? 'Position X' : 'Position Y'}</span>
                    <span>{Math.round(mediaTransform[key] * 100)}</span>
                  </div>
                  <input
                    type="range"
                    min={-50}
                    max={50}
                    step={1}
                    value={Math.round(mediaTransform[key] * 100)}
                    onChange={(event) =>
                      setSurfaceMediaTransform({
                        ...mediaTransform,
                        [key]: Number(event.target.value) / 100,
                      })
                    }
                    className="w-full accent-[#F24822]"
                  />
                </div>
              ))}
              <div className="mt-3 flex gap-1.5">
                <Button
                  className="!px-2.5 !py-1 !text-[12px]"
                  disabled={!previewMediaAspect}
                  onClick={() =>
                    setSurfaceMediaTransform({ scale: fillScale, offsetX: 0, offsetY: 0 })
                  }
                >
                  Fill Surface
                </Button>
                <Button
                  className="!px-2.5 !py-1 !text-[12px]"
                  onClick={() =>
                    setSurfaceMediaTransform({ scale: 1, offsetX: 0, offsetY: 0 })
                  }
                >
                  Reset
                </Button>
              </div>
              <p className="mt-2 font-mono text-[11px] leading-snug text-text-dimmer">
                확대·위치는 Surface UV에 고정되어 워핑과 함께 이동합니다.
              </p>
            </Panel>
          </div>

          <div className="flex-none">
            <Panel title="Bypass Warp">
              <Segmented
                value={bypassWarp ? 'on' : 'off'}
                onChange={(v) => setBypassWarp(v === 'on')}
                options={[
                  { value: 'off', label: 'Off' },
                  { value: 'on', label: 'On' },
                ]}
              />
            </Panel>
          </div>
        </aside>

        {/* ============ CENTER — surface 캔버스 ============ */}
        <main className="min-w-0 min-h-0 flex flex-col">
          <SurfaceCanvasV2 media={mediaTexture} />
        </main>

        {/* ============ RIGHT — 선택 점 + 프리셋 ============ */}
        <aside className="flex flex-col gap-3 min-h-0 overflow-hidden">
          <Panel
            title="Selected"
            trailing={
              <span className="font-mono text-[12px] font-semibold uppercase tracking-monoWide text-accent">
                {selectionLabel ?? '—'}
              </span>
            }
          >
            <div className="grid grid-cols-2 gap-2 mb-3">
              <CoordField
                label="X"
                disabled={selectedPoint === null}
                value={selectedPoint?.x ?? null}
                onCommit={(x) => commitSelectedPoint({ x })}
              />
              <CoordField
                label="Y"
                disabled={selectedPoint === null}
                value={selectedPoint?.y ?? null}
                onCommit={(y) => commitSelectedPoint({ y })}
              />
            </div>

            <div className="flex items-center gap-3">
              <DPad disabled={selection === null} onNudge={onNudgeDpad} />
              <div className="flex flex-col gap-1.5 flex-1 min-w-0">
                <span className="font-mono text-[11px] uppercase tracking-monoWide text-text-dim">
                  Step
                </span>
                <Segmented
                  value={nudgeStep}
                  onChange={setNudgeStep}
                  options={[
                    { value: 1, label: '1' },
                    { value: 10, label: '10' },
                    { value: 0.1, label: '.1' },
                  ]}
                />
                <span className="font-mono text-[11px] text-text-dimmer leading-tight">
                  ⇧×10 · ⌃÷10
                </span>
              </div>
            </div>
          </Panel>

          <Panel
            title="Presets"
            trailing={
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => {
                    if (presets.length === 0) {
                      toast('내보낼 preset이 없습니다.', 'info');
                      return;
                    }
                    void exportPresets();
                  }}
                  disabled={presets.length === 0}
                  title="모든 preset을 JSON 파일로 백업"
                  className={cn(
                    'px-2 py-0.5 rounded-sm border font-mono text-[12px] uppercase tracking-monoWide',
                    'transition-colors duration-fast ease-snap',
                    presets.length === 0
                      ? 'border-line text-text-dimmer cursor-not-allowed'
                      : 'border-line text-text-dim hover:text-text hover:border-line-strong',
                  )}
                >
                  ⤓ Export
                </button>
                <button
                  type="button"
                  onClick={() => importFileRef.current?.click()}
                  title="JSON 백업 파일에서 preset 불러오기"
                  className="px-2 py-0.5 rounded-sm border border-line text-text-dim hover:text-text hover:border-line-strong font-mono text-[12px] uppercase tracking-monoWide transition-colors duration-fast ease-snap"
                >
                  ⤒ Import
                </button>
                <span className="font-mono text-[12px] font-semibold uppercase tracking-monoWide text-text-dim ml-1">
                  {presets.length}
                </span>
              </div>
            }
          >
            <input
              ref={importFileRef}
              type="file"
              accept="application/json,.json"
              onChange={(e) => void handleImportFile(e)}
              className="hidden"
            />
            <div className="flex flex-col gap-1 max-h-[calc(100vh-420px)] min-h-[120px] overflow-auto">
              {presets.length === 0 && (
                <div className="font-mono text-[12px] text-text-dimmer py-3 text-center">
                  No presets. <Kbd>S</Kbd> to save.
                </div>
              )}
              {presets.map((p, idx) => (
                <PresetRow
                  key={p.id}
                  preset={p}
                  idx={idx + 1}
                  active={p.id === activePresetId}
                  onApply={() => void applyPresetById(p.id)}
                  onDuplicate={() => void duplicatePresetById(p.id)}
                  onDelete={() =>
                    void askConfirm({
                      title: p.protected ? 'Delete protected preset?' : 'Delete preset',
                      message: p.protected
                        ? `'${p.name}' is marked protected. Delete anyway?`
                        : `Delete preset '${p.name}'?`,
                      destructive: true,
                    }).then((ok) => {
                      if (ok) void deletePresetById(p.id);
                    })
                  }
                  onToggleProtected={() => void togglePresetProtected(p.id)}
                />
              ))}
            </div>
          </Panel>
        </aside>
      </div>

      {/* ============================================================
          Modal layer
          ============================================================ */}
      {pendingMeshDensity !== null && (
        <ConfirmDialog
          title="그리드 밀도 낮추기?"
          message={`밀도를 ${pendingMeshDensity}×${pendingMeshDensity}로 낮추면 셀 내부의 세부 변위가 소실될 수 있습니다.\nUndo (⌘Z)로 복구 가능합니다.`}
          destructive
          confirmLabel="낮추기"
          onConfirm={confirmMeshDensity}
          onCancel={cancelMeshDensity}
        />
      )}

      {mediaPickerFor !== null && (
        <TargetPickerModal
          kind="media"
          items={mediaList}
          selectedId={
            mediaPickerFor === 'preview'
              ? previewSource.kind === 'media'
                ? previewSource.mediaId
                : ''
              : content.type === 'media'
                ? content.mediaId
                : ''
          }
          onSelect={(id) => {
            if (mediaPickerFor === 'preview') {
              setPreviewSource({ kind: 'media', mediaId: id });
            } else {
              setSurfaceContent({ type: 'media', mediaId: id });
            }
          }}
          onClose={() => setMediaPickerFor(null)}
        />
      )}

      {promptState && (
        <PromptDialog
          title={promptState.title}
          initial={promptState.initial}
          submitLabel={promptState.submitLabel ?? 'OK'}
          onSubmit={(v) => {
            promptState.resolve(v);
            setPromptState(null);
          }}
          onCancel={() => {
            promptState.resolve(null);
            setPromptState(null);
          }}
        />
      )}

      {confirmState && (
        <ConfirmDialog
          title={confirmState.title}
          message={confirmState.message}
          destructive={confirmState.destructive ?? false}
          onConfirm={() => {
            confirmState.resolve(true);
            setConfirmState(null);
          }}
          onCancel={() => {
            confirmState.resolve(false);
            setConfirmState(null);
          }}
        />
      )}
    </div>
  );
}

type PromptState = {
  title: string;
  initial: string;
  submitLabel?: string;
  resolve: (value: string | null) => void;
};
type ConfirmState = {
  title: string;
  message: string;
  destructive?: boolean;
  resolve: (value: boolean) => void;
};

// =============================================================================
// Sub-components
// =============================================================================

function Topbar({
  deviceId,
  saveStatus,
  canUndo,
  canRedo,
  connectionStatus,
  onUndo,
  onRedo,
  onReset,
  onRevert,
  onSave,
  onSaveAs,
}: {
  deviceId?: string | undefined;
  saveStatus: 'saved' | 'unsaved' | 'new';
  canUndo: boolean;
  canRedo: boolean;
  connectionStatus: 'connecting' | 'online' | 'offline';
  onUndo: () => void;
  onRedo: () => void;
  onReset: () => void;
  onRevert: () => void;
  onSave: () => void;
  onSaveAs: () => void;
}) {
  const tone =
    connectionStatus === 'online'
      ? 'green'
      : connectionStatus === 'connecting'
        ? 'dim'
        : 'red';
  const statusText =
    connectionStatus === 'online'
      ? 'Server · Online'
      : connectionStatus === 'connecting'
        ? 'Server · Connecting'
        : 'Server · Offline';

  return (
    <header className="flex items-center justify-between px-5 py-3 border-b border-line flex-none">
      <div className="flex items-center gap-3">
        <h1 className="font-display text-[19px] font-bold">
          <span className="inline-flex items-center justify-center w-4 text-accent mr-2 text-[12px] leading-none align-middle">◢</span>
          Calibration
          <span className="ml-2 font-mono text-[12px] text-text-dim uppercase tracking-monoWide">
            v2
          </span>
          {deviceId && (
            <span className="ml-2 font-mono text-[12px] text-text-dim uppercase tracking-monoWide">
              device · {deviceId}
            </span>
          )}
        </h1>
        <StatusBadge tone={tone}>{statusText}</StatusBadge>
      </div>
      <div className="flex items-center gap-1.5">
        <SaveStatusBadge status={saveStatus} />
        <Button variant="ghost" onClick={onUndo} disabled={!canUndo} title="Undo ⌘Z">↶</Button>
        <Button variant="ghost" onClick={onRedo} disabled={!canRedo} title="Redo ⌘⇧Z">↷</Button>
        <Button variant="ghost" onClick={onRevert} disabled={saveStatus === 'saved'} title="Revert to last saved">Revert</Button>
        <Button variant="ghost" onClick={onReset} title="Reset (R)">Reset</Button>
        <Button
          variant={saveStatus === 'unsaved' ? 'primary' : 'default'}
          title="Save (S)"
          onClick={onSave}
          disabled={saveStatus === 'saved'}
        >
          Save
        </Button>
        <Button
          variant={saveStatus === 'new' ? 'primary' : 'default'}
          title="Save As (⇧S)"
          onClick={onSaveAs}
        >
          Save As…
        </Button>
      </div>
    </header>
  );
}

function PresetRow({
  preset,
  idx,
  active,
  onApply,
  onDuplicate,
  onDelete,
  onToggleProtected,
}: {
  preset: WarpPresetV2;
  idx: number;
  active: boolean;
  onApply: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
  onToggleProtected: () => void;
}) {
  const meta = new Date(preset.lastSavedAt).toISOString().replace('T', ' ').slice(0, 16);
  const maskedCount = preset.surfaces.filter((s) => s.mask !== null).length;
  return (
    <div
      className={cn(
        'grid grid-cols-[auto_1fr_auto] gap-3 items-center px-3 py-2.5 rounded border transition-all duration-fast ease-snap',
        active
          ? 'border-accent-ring bg-accent-soft'
          : 'border-line bg-panel-2 hover:border-line-strong hover:bg-panel-3',
      )}
    >
      <div
        className={cn(
          'w-7 h-7 border rounded-sm flex items-center justify-center font-mono text-[12px] bg-bg',
          active ? 'border-accent text-accent' : 'border-line text-text-dim',
        )}
      >
        {String(idx).padStart(2, '0')}
      </div>
      <div className="min-w-0">
        <div className="font-ui text-[16px] font-medium text-text mb-0.5 truncate flex items-center gap-1.5">
          {preset.name}
          <button
            type="button"
            onClick={onToggleProtected}
            className={cn(
              'text-[12px]',
              preset.protected ? 'text-accent' : 'text-text-dimmer hover:text-text-dim',
            )}
            title={preset.protected ? 'Protected (click to unlock)' : 'Unprotected (click to lock)'}
          >
            {preset.protected ? '🔒' : '🔓'}
          </button>
        </div>
        <div className="font-mono text-[12px] text-text-dim uppercase tracking-[0.08em] flex gap-2.5 flex-wrap">
          <span>{meta}</span>
          {active && (
            <span className="px-1.5 py-[1px] rounded-sm font-medium bg-[rgba(242,169,62,0.15)] text-accent">
              Active
            </span>
          )}
          {maskedCount > 0 && (
            <span className="px-1.5 py-[1px] rounded-sm font-medium bg-[rgba(125,211,252,0.12)] text-blue">
              Mask
            </span>
          )}
          {preset.bypassWarp && (
            <span className="px-1.5 py-[1px] rounded-sm font-medium bg-[rgba(192,132,252,0.12)] text-purple">
              Bypass
            </span>
          )}
        </div>
      </div>
      <div className="flex gap-1">
        {!active && (
          <PresetAction title="Apply" onClick={onApply}>
            ▸
          </PresetAction>
        )}
        <PresetAction title="Duplicate" onClick={onDuplicate}>
          ⎘
        </PresetAction>
        <PresetAction title="Delete" danger onClick={onDelete}>
          ✕
        </PresetAction>
      </div>
    </div>
  );
}

function PresetAction({
  children,
  title,
  danger = false,
  onClick,
}: {
  children: React.ReactNode;
  title: string;
  danger?: boolean;
  onClick?: () => void;
}) {
  return (
    <button
      title={title}
      type="button"
      onClick={onClick}
      className={cn(
        'w-7 h-7 border border-line bg-transparent text-text-dim rounded-sm',
        'flex items-center justify-center font-mono text-[13px] cursor-pointer',
        'transition-all duration-fast ease-snap',
        danger
          ? 'hover:text-red hover:border-red'
          : 'hover:text-text hover:border-line-strong',
      )}
    >
      {children}
    </button>
  );
}

/**
 * Editable coordinate input — 포커스 중 draft 유지, Enter/blur 커밋,
 * Escape 되돌림. [0,1] 클램프. (v1 CoordField 이식.)
 */
function CoordField({
  label,
  value,
  disabled,
  onCommit,
}: {
  label: string;
  value: number | null;
  disabled: boolean;
  onCommit: (v: number) => void;
}) {
  const display = value === null ? '' : value.toFixed(4);
  const [draft, setDraft] = useState<string | null>(null);
  const shown = draft ?? display;

  const commit = () => {
    if (draft === null) return;
    const parsed = Number.parseFloat(draft);
    if (Number.isFinite(parsed)) {
      onCommit(Math.max(0, Math.min(1, parsed)));
    }
    setDraft(null);
  };

  const onChange = (e: ChangeEvent<HTMLInputElement>) => setDraft(e.target.value);
  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      (e.currentTarget as HTMLInputElement).blur();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setDraft(null);
      (e.currentTarget as HTMLInputElement).blur();
    }
  };

  return (
    <Field
      label={label}
      accent
      value={shown}
      disabled={disabled || value === null}
      onChange={onChange}
      onKeyDown={onKeyDown}
      onBlur={commit}
      inputMode="decimal"
    />
  );
}

/** Surface 밖 좌표도 입력할 수 있는 마스크 중심 퍼센트 필드. */
function MaskPositionInput({
  value,
  onCommit,
}: {
  value: number;
  onCommit: (value: number) => void;
}) {
  const [draft, setDraft] = useState<string | null>(null);
  const commit = () => {
    if (draft === null) return;
    const parsed = Number.parseFloat(draft);
    if (Number.isFinite(parsed)) onCommit(parsed / 100);
    setDraft(null);
  };

  return (
    <label className="flex items-center gap-1 text-accent">
      <input
        type="number"
        step={1}
        value={draft ?? String(Math.round(value * 100))}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={commit}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            event.currentTarget.blur();
          } else if (event.key === 'Escape') {
            event.preventDefault();
            setDraft(null);
            event.currentTarget.blur();
          }
        }}
        aria-label="Mask position percent"
        className="w-16 rounded border border-line bg-panel-2 px-1.5 py-0.5 text-right font-mono text-[11px] text-accent outline-none focus:border-accent"
      />
      <span>%</span>
    </label>
  );
}

function PromptDialog({
  title,
  initial,
  submitLabel,
  onSubmit,
  onCancel,
}: {
  title: string;
  initial: string;
  submitLabel: string;
  onSubmit: (value: string) => void;
  onCancel: () => void;
}) {
  const [value, setValue] = useState(initial);
  const submit = () => {
    const trimmed = value.trim();
    if (trimmed) onSubmit(trimmed);
  };
  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center"
      {...backdropCloseProps(onCancel)}
    >
      <div
        className="bg-panel border border-line-strong rounded-md p-6 min-w-[504px] max-w-[624px] shadow-[0_20px_60px_rgba(0,0,0,0.6)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="font-display text-[22px] font-semibold mb-4">{title}</div>
        <input
          autoFocus
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              submit();
            } else if (e.key === 'Escape') {
              e.preventDefault();
              onCancel();
            }
          }}
          className="w-full px-3 py-2 mb-5 bg-panel-2 border border-line rounded text-[17px] font-mono text-text focus:outline-none focus:border-accent focus:shadow-[0_0_0_3px_rgba(242,169,62,0.12)]"
        />
        <div className="flex gap-2 justify-end">
          <Button onClick={onCancel}>취소</Button>
          <Button variant="primary" onClick={submit} disabled={!value.trim()}>
            {submitLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}

function ConfirmDialog({
  title,
  message,
  destructive,
  confirmLabel,
  onConfirm,
  onCancel,
}: {
  title: string;
  message: string;
  destructive: boolean;
  confirmLabel?: string;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center"
      {...backdropCloseProps(onCancel)}
    >
      <div
        className="bg-panel border border-line-strong rounded-md p-6 min-w-[504px] max-w-[624px] shadow-[0_20px_60px_rgba(0,0,0,0.6)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="font-display text-[22px] font-semibold mb-3">{title}</div>
        <div className="font-mono text-[14px] text-text-dim mb-5 whitespace-pre-wrap">
          {message}
        </div>
        <div className="flex gap-2 justify-end">
          <Button onClick={onCancel}>취소</Button>
          <Button variant={destructive ? 'danger' : 'primary'} onClick={onConfirm}>
            {confirmLabel ?? (destructive ? 'Delete' : 'OK')}
          </Button>
        </div>
      </div>
    </div>
  );
}
